import { useEffect, useRef, useState } from "react";
import { RefreshCw, Sparkles, ListChecks } from "lucide-react";
import { api } from "../lib/api";
import { fdate, run, useLoad } from "../lib/util";
import { Alert, Badge, Btn } from "../components/kit";
import { useSchool } from "./ctx";
import PlanEditor from "./PlanEditor";
import AdjustSetting from "./AdjustSetting";

const ST = { nao_gerado: ["Não gerada", "muted"], gerando: ["Gerando com IA…", "info"], pronto: ["Pronta", "green"], erro: ["Erro", "red"] };

export default function PlanPanel() {
  const { month, stock, tick, bump } = useSchool();
  const [plan, reload] = useLoad(async () => (await api.get(`/s/months/${month}/plan`)).data, [month, tick]);
  const synced = useRef("");
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (plan?.status === "gerando") {
      const t = setTimeout(async () => {
        const r = (await api.get(`/s/months/${month}/plan`)).data;
        if (r.status !== "gerando") bump();
        else reload();
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [plan, month, reload, bump]);
  useEffect(() => {
    const key = `${month}|${plan?.updated_at}`;
    if (plan?.status === "pronto" && stock?.comensais_configured && stock.distribution !== "per_capita" && synced.current !== key) {
      synced.current = key;
      bump();
    }
  }, [plan, stock, month, bump]);
  if (!plan || !stock) return null;
  const gen = () => {
    if (plan.manual && !window.confirm("Refazer a distribuição com a IA? As correções que você salvou serão substituídas.")) return;
    run(() => api.post(`/s/months/${month}/plan`), "Gerando distribuição per capita…").then((ok) => ok && reload());
  };
  const adjust = Object.fromEntries(Object.entries(stock.per_capita_adjust || {}));
  const adjNames = Object.entries(adjust).map(([p, f]) => `${(stock.rows.find((r) => r.product_id === p) || {}).name || "?"} ×${String(f).replace(".", ",")}`);
  const s = ST[plan.status] || ST.nao_gerado;
  return (
    <div className="rounded-xl border bg-white p-4 space-y-3" data-testid="plan-panel">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="w-4 h-4 text-[#FF6500]" />
        <span className="font-heading font-bold text-[#0B192C]">Distribuição per capita</span>
        <Badge tone={s[1]} data-testid="plan-status">{s[0]}</Badge>
        {plan.updated_at && <span className="text-xs text-slate-500">atualizada em {fdate(plan.updated_at)}</span>}
        {plan.manual && <Badge tone="info" data-testid="plan-manual-badge">Corrigida por você</Badge>}
        <div className="flex-1" />
        {plan.status === "pronto" && plan.plan && <Btn variant="outline" data-testid="plan-view-button" onClick={() => setEditing(true)}><ListChecks className="w-4 h-4" />Ver e corrigir distribuição</Btn>}
        <Btn variant="outline" data-testid="plan-generate-button" disabled={plan.status === "gerando"} onClick={gen}><RefreshCw className="w-4 h-4" />{plan.status === "pronto" ? "Refazer distribuição" : "Gerar distribuição"}</Btn>
      </div>
      <p className="text-xs text-slate-600">
        A IA lê o cardápio do mês, as fichas técnicas (per capita por aluno) e o guia de substituições. Quando o cardápio não corresponde ao que foi comprado, ela troca por um gênero do mesmo grupo. A quantidade usada em cada turno no dia é o per capita × o número de comensais do dia naquele turno (em kg ou na unidade do gênero). É refeita sozinha quando você confirma uma nota, o cardápio ou uma ficha.
      </p>
      {plan.status === "erro" && <Alert tone="red" testid="plan-error">{plan.error}</Alert>}
      {stock.adjust_settings && <AdjustSetting settings={stock.adjust_settings} />}
      {stock.distribution === "per_capita" ? (
        <div className="text-xs text-slate-600 space-y-1" data-testid="plan-summary">
          <div>{stock.per_capita_products.length} gênero(s) distribuídos pelo per capita.</div>
          {adjNames.length > 0 && <div className="text-[#FF6500]" data-testid="plan-adjust-list">Per capita aumentado para usar a sobra do mês (até ×{String(stock.adjust_settings?.max).replace(".", ",")}, sem passar do estoque comprado): {adjNames.join(", ")}</div>}
          {stock.missing_unit_grams.length > 0 && <div className="text-amber-700">Sem peso por unidade (distribuídos igualmente nos dias): {stock.missing_unit_grams.join(", ")}</div>}
          {(stock.substitutions || []).length > 0 && <div className="text-sky-700">{stock.substitutions.length} preparo(s) feito(s) com substituição por falta do produto nas notas — veja no Cardápio diário.</div>}
          {plan.plan?.notes?.length > 0 && <div>Observações da IA: {plan.plan.notes.join(" · ")}</div>}
        </div>
      ) : (
        <div className="text-xs text-amber-700" data-testid="plan-fallback">Enquanto a distribuição per capita não estiver pronta (são necessários cardápio, fichas técnicas e comensais do mês), as compras são distribuídas igualmente nos dias de merenda.</div>
      )}
      {editing && <PlanEditor initial={plan.plan} adjust={adjust} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); reload(); bump(); }} />}
    </div>
  );
}
